import store from 'redux/store';
import { deleteFolder } from 'redux/slices/lists/foldersSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditFolder } from './edit/showEditFolder';

type FolderEntity = Extract<ActionEntity, { type: 'folder' }>;

const isAllFolder = (entities: readonly ActionEntity[]): entities is readonly FolderEntity[] => entities.every((entity) => entity.type === 'folder');

/**
 * Action definitions for folders. `createFolder` / `addToTransportFolder` are
 * scoped (always present, fully-qualified); `edit` (single-only) and `delete`
 * are contextual. No `duplicate` — folders never had one. create/edit fire no
 * refresh (the slice reducers update the list in place); delete refetches so the
 * pagination total stays in sync (the reducer filter alone left it stale).
 */
export const folderActionDefinitions: ActionDefinitions = [
  {
    id: 'createFolder',
    scope: 'type',
    label: 'New Folder',
    resolve: (ctx) => () => void showEditFolder({ mode: 'create', createIn: ctx.createIn }).then(ctx.refresh),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllFolder(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditFolder({ mode: 'edit', id: only.id }).then(refresh);
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllFolder(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteFolder(entity.id)), {
        refresh,
        confirmBody: `Are you sure you want to delete ${entities.length === 1 ? 'this folder' : `these ${entities.length} folders`}?`,
      });
    },
  },
  {
    id: 'addToTransportFolder',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllFolder(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'folder',
        entities.map((entity) => entity.id),
        { objectLabel: single ? single.name : 'Selected folders' }
      );
    },
  },
];
